import React, { useEffect, useMemo, useRef, useState } from "react";

// The one dropdown used by every picker in a time-entry row.
//
// Its whole contract is a single rule: THE HIGHLIGHTED ROW IS WHAT YOU GET.
// Tab and Enter both commit the highlight; Esc discards it. Focusing the field
// highlights NOTHING, which is what makes Tab-commits safe -- tabbing across a
// row that already has values cannot rewrite them, because there is no
// highlight until you arrow or type.
const LIMIT = 50;

// `onCreate` opts a field into a create row at the bottom of the list: the way
// out of "No match" for the one picker whose list you are allowed to add to.
// It is reached with the arrows like any other row and fired with Enter --
// NEVER with Tab, which commits real options only. Tab is how you cross this
// grid, and a Tab that could open a modal would fire one by accident on every
// half-filled row.
// `locked` is for a value that something else on the row now dictates -- the
// Year End once the line names a task. It stays the SAME <input>, readOnly and
// out of the Tab order, rather than being swapped for one: React removes the
// element it replaces, and removing the focused element drops focus to <body>,
// which sends the next Tab back to the top of the page. That matters because
// the keystroke that locks this cell is usually the Tab arriving in it.
export default function ComboField({
  value, options, onChange, ariaLabel, disabled, placeholder = "—",
  onCreate, createLabel, emptyText = "No match", locked, lockedTitle, skipTab,
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const listRef = useRef(null);

  const selectedLabel = useMemo(
    () => options.find((o) => String(o.value) === String(value))?.label || "",
    [options, value]
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
    return list.slice(0, LIMIT);
  }, [options, query]);

  // Keep the highlight visible: the list scrolls at 220px but holds up to 50
  // rows, so arrowing down walks it straight out of the viewport otherwise.
  useEffect(() => {
    if (!open || hi < 0) return;
    listRef.current?.querySelector('[data-hi="true"]')?.scrollIntoView({ block: "nearest" });
  }, [hi, open]);

  // The create row sits one past the last match, so it is the row the
  // highlight lands on when a typed query matches nothing -- type a name,
  // press Enter, fill in the form.
  const createIndex = onCreate ? matches.length : -1;
  const lastIndex = onCreate ? matches.length : matches.length - 1;

  const close = () => { setOpen(false); setQuery(""); setHi(-1); };

  const commit = (opt) => { onChange(opt.value); close(); };

  // Commit whatever is highlighted, if anything. Returns whether it acted, so
  // Enter can swallow the keystroke and Tab can let it through. `viaEnter`
  // gates the create row: Tab must never fire it.
  const commitHighlight = (viaEnter) => {
    if (hi >= 0 && hi < matches.length) { commit(matches[hi]); return true; }
    if (viaEnter && createIndex >= 0 && hi === createIndex) {
      const typed = query;
      close();
      onCreate(typed);
      return true;
    }
    return false;
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) setOpen(true);
      setHi((h) => {
        if (h < 0) return 0;                                  // both arrows enter at the top
        const next = e.key === "ArrowDown" ? h + 1 : h - 1;
        return Math.max(0, Math.min(next, lastIndex)); // clamp, never wrap
      });
      return;
    }
    if (e.key === "Enter") { if (commitHighlight(true)) e.preventDefault(); return; }
    if (e.key === "Escape") { e.preventDefault(); close(); return; }
    if (e.key === "Tab") { commitHighlight(false); }  // no preventDefault: focus moves on
  };

  return (
    <div className="te-cp">
      <input
        className={"te-in" + (locked ? " te-in-locked" : "")}
        aria-label={ariaLabel}
        role="combobox"
        aria-expanded={locked ? undefined : open}
        aria-controls={open ? "te-cp-list" : undefined}
        aria-autocomplete="list"
        disabled={disabled}
        readOnly={locked || undefined}
        tabIndex={(locked || skipTab) ? -1 : undefined}
        title={locked ? lockedTitle : undefined}
        placeholder={locked ? undefined : placeholder}
        // A locked cell shows what is STORED on the row, never what the task
        // says it should be. Old entries were deliberately not re-stamped, so
        // the two can disagree -- and printing the task's year over a
        // different stored one would make the grid lie about the roll-up.
        // Re-picking the task is the remedy: that stamps, and they agree again.
        value={locked ? (selectedLabel || (value ?? "")) : (open ? query : selectedLabel)}
        onKeyDown={locked ? undefined : onKeyDown}
        onFocus={locked ? undefined : () => { setQuery(""); setHi(-1); setOpen(true); }}
        onChange={locked ? undefined : (e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHi(0);            // typing means intent: highlight the top match
        }}
        // Closing synchronously is safe: an option's onMouseDown calls
        // preventDefault, so clicking one never blurs the input in the first
        // place. (The old ClientPicker deferred this by 120ms to dodge exactly
        // that race, and left a timer running after every blur.)
        onBlur={locked ? undefined : close}
      />
      {!locked && open && (matches.length > 0 || query || onCreate) && (
        <div className="te-cp-list" id="te-cp-list" role="listbox" ref={listRef}>
          {matches.map((o, i) => (
            <div key={o.value} role="option" aria-selected={i === hi}
              data-hi={i === hi ? "true" : undefined}
              className={"te-cp-item" + (i === hi ? " te-cp-hi" : "")}
              onMouseDown={(e) => { e.preventDefault(); commit(o); }}>
              {o.label}
            </div>
          ))}
          {query && matches.length === 0 && <div className="te-cp-empty">{emptyText}</div>}
          {onCreate && (
            <div role="option" aria-selected={hi === createIndex}
              data-hi={hi === createIndex ? "true" : undefined}
              className={"te-cp-item te-cp-new" + (hi === createIndex ? " te-cp-hi" : "")}
              onMouseDown={(e) => { e.preventDefault(); const typed = query; close(); onCreate(typed); }}>
              {createLabel ? createLabel(query) : "+ New"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
